/**
 * The one clock behind every lot countdown on the web (#191).
 *
 * The server owns the clock (fn_LotClock). Every lot row it sends - REST or hub - carries the
 * instant the countdown reaches 00:00 (endsAtUtc, already cut short by the bidding window, #190)
 * and its own clock when the row was produced (serverNowUtc). The screens used to rebuild a clock
 * from the "mm:ss" duration instead, each in its own way, so a duplicate or late push moved the
 * countdown, a pause was ignored, and some screens promoted a lot to Live on the device clock.
 *
 * The rule, applied by nextClock():
 *   - Deadline (client epoch ms) = the moment the payload arrived + (endsAtUtc - serverNowUtc),
 *     computed per payload: the difference cancels the device clock's error, whatever it is.
 *     remainingBidTime is used only when either field is missing (an older API).
 *   - The same endsAtUtc never moves a running countdown, except to adopt a sample that is clearly
 *     better (arrived with less latency) or a device clock that was changed.
 *   - Newest wins: a row produced before the last one applied for that lot is ignored.
 *   - Paused is frozen at remainingBidTime; anything else that is not Live has no clock at all.
 *
 * This module imports nothing, so notifications.js (which helper.js and apiClient.js import) and the
 * tests can load it without axios.
 */

/**
 * The Worker pushes the bid status this long after a running lot's countdown ends. A lot is over at
 * 00:00 (#191 reopen): the server no longer takes a bid in the second after it, so a Closed row is
 * always final, and this was 2500 while that second existed (CLOSE_GRACE_MS).
 */
export const CLOSE_PUSH_DELAY_MS = 1500;
/** When a lot page asks the server about a lot whose countdown reached 00:00 with no close yet. */
export const RECHECK_AFTER_ZERO_MS = 2500;
/**
 * How far a remainingBidTime-only timer may drift from a running countdown before the countdown is
 * reset. Only for an older API: a payload with endsAtUtc is reconciled by the server instant.
 */
export const TIMER_DRIFT_MS = 2000;

/** A sample of the same deadline this much earlier arrived with less latency, and is adopted. */
const EARLIER_SAMPLE_MS = 250;
/** A sample of the same deadline this far off means the device clock was changed. */
const REANCHOR_MS = 5000;
/** Lands a tick just after the second boundary rather than just before it. */
const TICK_SLACK_MS = 15;

/** Fields that describe a lot's clock rather than the lot, and must never seed a clock. */
export const CLOCK_FIELDS = ['remainingBidTime', 'recievedAt', 'timeLeft', 'endsAtUtc', 'serverNowUtc'];

/**
 * Reads a date the API sent. They are UTC without a zone ("2026-10-09T15:00:00"), which the
 * browser would otherwise take for local time - hence the codebase's dayjs(value + "Z"). The lot
 * clock fields carry a "Z", and are read the same way.
 *
 * @param {string} value The date as the API sent it.
 * @returns {number} Epoch milliseconds, or NaN when it is not a date.
 */
export const parseServerUtc = (value) => {
    if (typeof value !== 'string' || !value) return NaN;
    const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(value);
    return Date.parse(hasZone ? value : `${value}Z`);
};

/**
 * Reads the server's "mm:ss" timer. Minutes may exceed 59 (a BidTime of an hour or more).
 *
 * @param {*} value The remainingBidTime of a row or pushed item.
 * @returns {number|null} Seconds, or null when there is no usable timer.
 */
export const parseMmSs = (value) => {
    if (typeof value !== 'string') return null;
    const match = /^(\d+):(\d{1,2})$/.exec(value.trim());
    if (!match) return null;
    return Number(match[1]) * 60 + Number(match[2]);
};

/**
 * Formats seconds the way the server does, clamped at 00:00. Minutes are not wrapped at 60, so
 * two hours read "120:00".
 *
 * @param {number} seconds Seconds left; fractions are dropped.
 * @returns {string} "mm:ss".
 */
export const formatMmSs = (seconds) => {
    const total = Math.max(0, Math.floor(Number(seconds) || 0));
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
};

/**
 * Formats seconds as Auction Details shows a team's time left (#237): hh:mm:ss from an hour,
 * mm:ss under it, clamped at 00:00.
 *
 * @param {number} seconds Seconds left; fractions are dropped.
 * @returns {string} "hh:mm:ss" or "mm:ss".
 */
export const formatHms = (seconds) => {
    const total = Math.max(0, Math.floor(Number(seconds) || 0));
    const pad = (n) => String(n).padStart(2, '0');
    const minutesAndSeconds = `${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
    return total >= 3600 ? `${pad(Math.floor(total / 3600))}:${minutesAndSeconds}` : minutesAndSeconds;
};

/**
 * The whole seconds left before a deadline, rounded up like the server's remainingBidTime: a lot
 * with 0.2 s left shows 00:01, and 00:00 only once it has run out.
 *
 * @param {number|null} endsAt The deadline, client epoch ms.
 * @param {number} [now] Date.now().
 * @returns {number} Seconds, never below 0; 0 without a deadline.
 */
export const remainingSeconds = (endsAt, now = Date.now()) => {
    if (!Number.isFinite(endsAt)) return 0;
    return Math.max(0, Math.ceil((endsAt - now) / 1000));
};

/**
 * How long until the countdown shows its next value: the next whole-second boundary before the
 * deadline, plus a few ms so the tick lands after it.
 *
 * @param {number|null} endsAt The deadline, client epoch ms.
 * @param {number} [now] Date.now().
 * @returns {number|null} Milliseconds, or null once the countdown is at 00:00.
 */
export const msToNextTick = (endsAt, now = Date.now()) => {
    if (!Number.isFinite(endsAt)) return null;
    const left = endsAt - now;
    if (left <= 0) return null;
    return left - (Math.ceil(left / 1000) - 1) * 1000 + TICK_SLACK_MS;
};

/**
 * Whether a row was produced before the last one applied for its lot. An equal instant is not
 * older (the REST page and a push of the same Worker pass), and a row without serverNowUtc - an
 * older API - never is.
 *
 * @param {object} item A row or pushed item.
 * @param {number|null} lastServerNowMs The serverNowUtc last applied for the lot, epoch ms.
 * @returns {boolean}
 */
export const isOlder = (item, lastServerNowMs) => {
    const serverNow = parseServerUtc(item?.serverNowUtc);
    return Number.isFinite(serverNow) && Number.isFinite(lastServerNowMs) && serverNow < lastServerNowMs;
};

/**
 * Whether a status ends a lot. #191 reopen: always for good - the server stops taking bids at 00:00,
 * so a Closed row can no longer be undone by a bid in the second after it (isFinalClose, which
 * treated such a row as provisional, is gone).
 */
export const isClosedStatus = (status) => status === 'Closed' || status === 'Archive';

/**
 * A copy of a row without its clock fields - for router state, which survives a browser reload and
 * must never be where a countdown comes from.
 *
 * @param {object} row A lot row.
 * @returns {object} The row without remainingBidTime, endsAtUtc, serverNowUtc and the old
 *   client-side recievedAt / timeLeft.
 */
export const withoutClockFields = (row) => {
    if (!row || typeof row !== 'object') return row;
    const copy = { ...row };
    CLOCK_FIELDS.forEach((field) => delete copy[field]);
    return copy;
};

const sameClock = (a, b) =>
    Boolean(a) &&
    ['status', 'endsAt', 'endsAtUtc', 'serverNow', 'frozenSeconds'].every((field) =>
        Object.is(a[field], b[field])
    );

/**
 * The clock of one lot after a row the server has just sent.
 *
 * @param {{status: string, endsAt: number|null, endsAtUtc: number|null, serverNow: number|null,
 *   frozenSeconds: number|null}|null|undefined} prev The lot's clock so far.
 * @param {object} item The row: status, remainingBidTime and, from a current API, endsAtUtc and
 *   serverNowUtc.
 * @param {number} receivedAt Date.now() at the moment the row arrived.
 * @returns {object} The new clock - endsAt is the deadline in client epoch ms, endsAtUtc and
 *   serverNow the server's instants, frozenSeconds a Paused lot's time - or prev itself when the row
 *   is older than prev or changes nothing.
 */
export const nextClock = (prev, item, receivedAt) => {
    if (prev && isOlder(item, prev.serverNow)) return prev;

    const status = item?.status;
    const itemServerNow = parseServerUtc(item?.serverNowUtc);
    const serverNow = Number.isFinite(itemServerNow) ? itemServerNow : prev?.serverNow ?? null;
    let next;

    if (status === 'Live') {
        const endsAtUtc = parseServerUtc(item?.endsAtUtc);
        const running = prev?.status === 'Live' && Number.isFinite(prev.endsAt) ? prev.endsAt : null;

        if (Number.isFinite(endsAtUtc) && Number.isFinite(itemServerNow)) {
            const sample = receivedAt + (endsAtUtc - itemServerNow);
            let endsAt = sample;
            if (running !== null && prev.endsAtUtc === endsAtUtc) {
                // The same server instant: keep the countdown that is running, unless this sample
                // came with clearly less latency, or the device clock has moved.
                const diff = sample - running;
                endsAt = diff < -EARLIER_SAMPLE_MS || Math.abs(diff) > REANCHOR_MS ? sample : running;
            }
            next = { status, endsAt, endsAtUtc, serverNow, frozenSeconds: null };
        } else {
            // An older API: only the rounded "mm:ss" is known, so small differences are noise.
            const seconds = parseMmSs(item?.remainingBidTime);
            if (seconds === null) {
                // No timer in this payload is not the same as no time left: keep the running one.
                next = running !== null
                    ? { ...prev, serverNow }
                    : { status, endsAt: null, endsAtUtc: null, serverNow, frozenSeconds: null };
            } else {
                const sample = receivedAt + seconds * 1000;
                const keep = running !== null && Math.abs(running - sample) <= TIMER_DRIFT_MS;
                next = {
                    status,
                    endsAt: keep ? running : sample,
                    endsAtUtc: keep ? prev.endsAtUtc : null,
                    serverNow,
                    frozenSeconds: null,
                };
            }
        }
    } else if (status === 'Paused') {
        const frozen = parseMmSs(item?.remainingBidTime);
        next = {
            status,
            endsAt: null,
            endsAtUtc: null,
            serverNow,
            frozenSeconds: frozen !== null ? frozen : prev?.frozenSeconds ?? null,
        };
    } else {
        next = { status: status ?? null, endsAt: null, endsAtUtc: null, serverNow, frozenSeconds: null };
    }

    return sameClock(prev, next) ? prev : next;
};
